import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  deleteDoc, 
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  getDoc,
  increment,
  setDoc,
  runTransaction,
  onSnapshot,
  startAfter,
  limitToLast
} from 'firebase/firestore';
import { db, auth } from './config'; // Update this line to use existing config

// Products Services
export const addProduct = async (productData) => {
  try {
    const productsRef = collection(db, 'products');
    const enrichedProductData = {
      ...productData,
      createdAt: serverTimestamp(),
      price: Number(productData.price),
      offerPrice: productData.isOffer ? Number(productData.offerPrice) : null,
      amanPrice: productData.isAman ? Number(productData.amanPrice) : null,
      wholesaleDiscount: Number(productData.wholesaleDiscount) || 0
    };
    
    await addDoc(productsRef, enrichedProductData);
    return { success: true };
  } catch (error) {
    console.error('Error adding product:', error);
    return { success: false, error: error.message };
  }
};

export const getProducts = async (categoryFilter = null) => {
  try {
    let productsQuery;
    
    if (categoryFilter) {
      productsQuery = query(
        collection(db, 'products'),
        where('category', '==', categoryFilter),
        orderBy('createdAt', 'desc')
      );
    } else {
      productsQuery = query(
        collection(db, 'products'),
        orderBy('createdAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(productsQuery);
    const products = [];
    
    querySnapshot.forEach((doc) => {
      products.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, products };
  } catch (error) {
    console.error('Error getting products:', error);
    return { success: false, error: error.message };
  }
};

export const deleteProduct = async (productId) => {
  try {
    await deleteDoc(doc(db, 'products', productId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting product:', error);
    return { success: false, error: error.message };
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const productRef = doc(db, 'products', productId);
    const enrichedProductData = {
      ...productData,
      updatedAt: serverTimestamp(),
      price: Number(productData.price),
      offerPrice: productData.isOffer ? Number(productData.offerPrice) : null,
      amanPrice: productData.isAman ? Number(productData.amanPrice) : null,
      wholesaleDiscount: Number(productData.wholesaleDiscount) || 0
    };
    
    await updateDoc(productRef, enrichedProductData);
    return { success: true };
  } catch (error) {
    console.error('Error updating product:', error);
    return { success: false, error: error.message };
  }
};

// Categories Services
export const addCategory = async (categoryData) => {
  try {
    const docRef = await addDoc(collection(db, 'categories'), {
      ...categoryData,
      createdAt: new Date()
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding category:', error);
    return { success: false, error: error.message };
  }
};

export const getCategories = async () => {
  try {
    const querySnapshot = await getDocs(
      query(collection(db, 'categories'), orderBy('createdAt', 'desc'))
    );
    const categories = [];
    
    querySnapshot.forEach((doc) => {
      categories.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, categories };
  } catch (error) {
    console.error('Error getting categories:', error);
    return { success: false, error: error.message };
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    await deleteDoc(doc(db, 'categories', categoryId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting category:', error);
    return { success: false, error: error.message };
  }
};

export const updateCategory = async (categoryId, updateData) => {
  try {
    await updateDoc(doc(db, 'categories', categoryId), {
      ...updateData,
      updatedAt: new Date()
    });

    return { success: true };
  } catch (error) {
    // Error handling for updating category
    return { success: false, error: error.message };
  }
};

// Orders Services
export const addOrder = async (orderData) => {
  try {
    // Use Firestore counter for unique, sequential order numbers
    const nextOrderNumber = await getNextOrderNumber('orders');

    // Sanitize and prepare order data
    const sanitizedItems = orderData.items.map(item => ({
      id: item.id,
      name: item.name || 'Unnamed Product',
      quantity: Number(item.quantity) || 1,
      price: Number(item.price) || 0,
      offerPrice: item.isOffer ? Number(item.offerPrice) || Number(item.price) : null,
      isOffer: Boolean(item.isOffer),
      wholesaleDiscount: Number(item.wholesaleDiscount) || 0,
      isWholesaleApplied: Boolean(item.isWholesaleApplied),
      finalPrice: Number(item.finalPrice) || Number(item.price),
      imageUrl: item.imageUrl || null,
      marketerId: item.marketerId || null,
      size: item.size || null,
      color: item.color || null
    }));

    // Create customer info object - our single source of truth for customer data
    const customerInfo = {
      name: orderData.clientName || '',
      phone: orderData.clientPhone || '',
      address: {
        street: orderData.clientAddress?.street || '',
        city: orderData.clientAddress?.city || '',
        country: orderData.clientAddress?.country || '',
        postalCode: orderData.clientAddress?.postalCode || ''
      },
      email: orderData.clientEmail || '',
      notes: orderData.clientNotes || ''
    };

    const marketerIdValue = (typeof orderData.marketerId !== 'undefined' && orderData.marketerId !== undefined)
      ? orderData.marketerId
      : (typeof auth.currentUser?.uid !== 'undefined' && auth.currentUser?.uid !== undefined)
        ? auth.currentUser.uid
        : null;
    const sanitizedOrder = {
      ...orderData,
      orderNumber: nextOrderNumber, // Use the counter-based order number
      items: sanitizedItems,
      customerInfo: customerInfo, // Single source of truth for customer data
      totalAmount: Number(orderData.totalAmount) || 0,
      createdAt: serverTimestamp(),
      status: orderData.status || 'pending',
      isWholesaleOrder: Boolean(orderData.isWholesaleOrder),
      marketerId: marketerIdValue
    };

    const docRef = await addDoc(collection(db, 'orders'), sanitizedOrder);
    return { success: true, orderId: docRef.id, orderNumber: nextOrderNumber };
  } catch (error) {
    console.error('Error adding order:', error);
    return { success: false, error: error.message };
  }
};

// Update order status with enhanced logging and UID handling
export const updateOrderStatus = async (orderId, newStatus) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error('Order not found');
    }

    const orderData = orderDoc.data();

    // Update order status
    await updateDoc(orderRef, {
      status: newStatus,
      updatedAt: serverTimestamp()
    });

    // Update marketer statistics if order is approved/shipped/returned
    if (['delivered', 'shipped', 'returned'].includes(newStatus)) {
      const marketerProductsMap = {};
      
      for (const item of orderData.items) {
        if (!item.marketerId) continue;
        
        if (!marketerProductsMap[item.marketerId]) {
          marketerProductsMap[item.marketerId] = [];
        }
        marketerProductsMap[item.marketerId].push({
          ...item,
          marketerUid: item.marketerId
        });
      }

      // Update statistics for each marketer
      await Promise.all(
        Object.entries(marketerProductsMap).map(([marketerUid, items]) =>
          updateMarketerStatistics(marketerUid, items)
        )
      );
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating order status:', error);
    return { success: false, error: error.message };
  }
};

// Update marketer statistics function to work without validation
async function updateMarketerStatistics(marketerId, items) {
  try {
    const statsRef = doc(db, 'marketerStats', marketerId);
    const statsDoc = await getDoc(statsRef);
    
    // Create stats document if it doesn't exist
    if (!statsDoc.exists()) {
      await initializeMarketerStats(marketerId);
    }

    // Calculate totals
    const orderTotal = items.reduce((total, item) => {
      const itemTotal = (Number(item.finalPrice) || 0) * (Number(item.quantity) || 1);
      return total + itemTotal;
    }, 0);

    const commission = items.reduce((total, item) => {
      const itemCommission = ((Number(item.basePrice) || 0) - (Number(item.promotionalPrice) || 0)) * 
                           (Number(item.quantity) || 1);
      return total + itemCommission;
    }, 0);

    // Update statistics atomically
    await updateDoc(statsRef, {
      total_sales_amount: increment(orderTotal),
      total_confirmed_orders: increment(1),
      total_commission: increment(commission),
      updatedAt: serverTimestamp()
    });

    // Statistics updated successfully
    
  } catch (error) {
    // Error handling for updating marketer statistics
    throw error;
  }
}

// Subscribe to marketer orders with pagination - fetch orders specific to a marketer
export const subscribeToMarketerOrders = (marketerId, callback, pageSize = 10, lastVisible = null) => {
  try {
    if (!marketerId) {
      // MarketerId is required to subscribe to orders
      return () => {};
    }

    // Subscribing to orders for marketer
    
    // Build query with pagination
    let baseQuery = query(
      collection(db, 'orders'),
      where('marketerId', '==', marketerId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );
    
    // If we have a lastVisible document, paginate from there
    const paginatedQuery = lastVisible ? 
      query(baseQuery, startAfter(lastVisible)) : 
      baseQuery;
    
    const unsubscribe = onSnapshot(paginatedQuery, async (querySnapshot) => {
      const orders = await Promise.all(querySnapshot.docs.map(async (docSnapshot) => {
        const data = docSnapshot.data();
        const createdAtDate = data.createdAt?.seconds ? 
          new Date(data.createdAt.seconds * 1000) : 
          new Date();

        // Use ONLY customerInfo for customer data - this is our single source of truth
        const customerInfo = data.customerInfo || { 
          name: 'N/A', 
          phone: 'N/A', 
          address: 'N/A',
          email: 'N/A'
        };

        // Format address if it's an object
        let formattedAddress = customerInfo.address;
        if (typeof customerInfo.address === 'object') {
          formattedAddress = Object.entries(customerInfo.address)
            .filter(([_, value]) => value)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ') || 'N/A';
        }

        // Calculate order total from items if not available
        const orderTotal = data.totalAmount || calculateOrderTotal(data.items || []);

        return {
          id: docSnapshot.id,
          orderNumber: data.orderNumber || 0,
          status: data.status || 'pending',
          items: data.items || [],
          totalAmount: orderTotal,
          createdAt: createdAtDate,
          formattedDate: createdAtDate.toLocaleDateString(),
          customerName: customerInfo.name,
          customerPhone: customerInfo.phone,
          customerEmail: customerInfo.email,
          customerAddress: formattedAddress,
          customerNotes: customerInfo.notes || '',
          admin_note: data.admin_note || '',
          marketerId: data.marketerId
        };
      }));

      // Include pagination metadata in the callback
      const lastDoc = querySnapshot.docs.length > 0 ?
        querySnapshot.docs[querySnapshot.docs.length - 1] : null;
      
      const hasMore = querySnapshot.docs.length === pageSize;
      
      callback({
        orders,
        lastVisible: lastDoc,
        hasMore: hasMore
      });
    }, (error) => {
      console.error('Error in marketer orders subscription:', error);
      callback({
        orders: [],
        lastVisible: null,
        hasMore: false
      });
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up marketer orders subscription:', error);
    return () => {};
  }
};

// Subscribe to all orders with pagination (for admin dashboard)
export const subscribeToAdminOrders = (callback, pageSize = 10, lastVisible = null, filters = {}, firstVisible = null) => {
  try {
    console.log(`Subscribing to admin orders, pageSize: ${pageSize}, hasLastVisible: ${!!lastVisible}, hasFirstVisible: ${!!firstVisible}`);
    
    let queryConstraints = [];

    // Enhanced marketer filter logic
    if (filters.marketerId === '__ALL_MARKETERS__') {
      // Only orders with marketerId present
      queryConstraints.push(where('marketerId', '!=', null));
      queryConstraints.push(orderBy('marketerId'));
      queryConstraints.push(orderBy('createdAt', filters.sortDirection || 'desc'));
    } else if (filters.marketerId === '__SHOP__') {
      // Only orders with no marketerId
      queryConstraints.push(where('marketerId', '==', null));
      queryConstraints.push(orderBy('createdAt', filters.sortDirection || 'desc'));
    } else if (filters.marketerId && filters.marketerId !== 'allOrders') {
      queryConstraints.push(where('marketerId', '==', filters.marketerId));
      if (filters.sortField === 'status') {
        queryConstraints.push(orderBy('status', filters.sortDirection || 'asc'));
        queryConstraints.push(orderBy('createdAt', 'desc'));
      } else {
        // Default to sorting by date
        queryConstraints.push(orderBy('createdAt', filters.sortDirection || 'desc'));
      }
    } else if (filters.status && filters.status !== 'all') {
      queryConstraints.push(orderBy('status', 'asc'));
      queryConstraints.push(orderBy('createdAt', 'desc'));
      queryConstraints.push(where('status', '==', filters.status));
    } else {
      queryConstraints.push(orderBy('createdAt', filters.sortDirection || 'desc'));
    }
    
    // Handle pagination differently based on whether we're going forward or backward
    if (firstVisible && !lastVisible) {
      // Going to previous page - we use the firstVisible document as a reference
      // and limitToLast to get the page before it
      const prevPageQuery = query(
        collection(db, 'orders'),
        ...queryConstraints,
        startAfter(firstVisible),
        limit(pageSize)
      );
      
      // First get the documents that would come after our target page
      getDocs(prevPageQuery).then(async (snapshot) => {
        // Now construct a query to get the page we want
        const mainQueryConstraints = [...queryConstraints];
        
        if (snapshot.docs.length > 0) {
          // If there are docs after our firstVisible, we need to get the page before firstVisible
          mainQueryConstraints.push(limit(pageSize));
        } else {
          // If there are no docs after firstVisible, it means we're at the last page
          // So we just get the last pageSize documents
          mainQueryConstraints.push(limitToLast(pageSize));
        }
        
        const mainQuery = query(collection(db, 'orders'), ...mainQueryConstraints);
        
        // Set up the snapshot listener for the main query
        return setupSnapshotListener(mainQuery, callback);
      });
      
      // Return a dummy unsubscribe function for now
      // The real one will be returned by setupSnapshotListener
      return () => {};
    } else {
      // Going to next page or initial load
      if (lastVisible) {
        queryConstraints.push(startAfter(lastVisible));
      }
      
      queryConstraints.push(limit(pageSize));
      
      // Build the query
      const ordersQuery = query(collection(db, 'orders'), ...queryConstraints);
      
      // Set up the snapshot listener
      return setupSnapshotListener(ordersQuery, callback);
    }
  } catch (error) {
    console.error('Error in subscribeToAdminOrders:', error);
    // Error handling for setting up admin orders subscription
    callback({
      orders: [],
      firstVisible: null,
      lastVisible: null,
      hasMore: false
    });
    return () => {};
  }
  
  // Helper function to set up the snapshot listener
  function setupSnapshotListener(ordersQuery, callback) {
    return onSnapshot(ordersQuery, async (querySnapshot) => {
      const orders = await Promise.all(querySnapshot.docs.map(async (docSnapshot) => {
        const data = docSnapshot.data();
        const createdAtDate = data.createdAt?.seconds ? 
          new Date(data.createdAt.seconds * 1000) : 
          new Date();
        
        // Use ONLY customerInfo for customer data - this is our single source of truth
        const customerInfo = data.customerInfo || { 
          name: 'N/A', 
          phone: 'N/A', 
          address: 'N/A',
          email: 'N/A'
        };

        // Format address if it's an object
        let formattedAddress = customerInfo.address;
        if (typeof customerInfo.address === 'object') {
          formattedAddress = Object.entries(customerInfo.address)
            .filter(([_, value]) => value)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ') || 'N/A';
        }

        // Fetch marketer details
        let marketerDetails = { name: 'N/A', email: 'N/A' };
        if (data.marketerId) {
          try {
            const marketerDoc = await getDoc(doc(db, 'users', data.marketerId));
            if (marketerDoc.exists()) {
              marketerDetails = {
                name: marketerDoc.data().name || 'N/A',
                email: marketerDoc.data().email || 'N/A'
              };
            }
          } catch (error) {
            console.error('Error fetching marketer details:', error);
          }
        }

        // Calculate order total from items if not available
        const orderTotal = data.totalAmount || calculateOrderTotal(data.items || []);
        const commission = data.totalCommission !== undefined ? 
          data.totalCommission : 0;

        return {
          id: docSnapshot.id,
          orderNumber: data.orderNumber || 0,
          status: data.status || 'pending',
          items: data.items || [],
          totalAmount: orderTotal,
          commission: commission,
          createdAt: createdAtDate,
          formattedDate: createdAtDate.toLocaleDateString(),
          customerName: customerInfo.name,
          customerPhone: customerInfo.phone,
          customerEmail: customerInfo.email,
          customerAddress: formattedAddress,
          customerNotes: customerInfo.notes || '',
          admin_note: data.admin_note || '',
          marketerId: data.marketerId,
          marketerName: marketerDetails.name,
          marketerEmail: marketerDetails.email,
          customerInfo: {
            ...customerInfo,
            address: formattedAddress
          }
        };
      }));

      // Include pagination metadata in the callback
      const firstDoc = querySnapshot.docs.length > 0 ? querySnapshot.docs[0] : null;
      const lastDoc = querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null;
      const hasMore = querySnapshot.docs.length === pageSize;
      
      callback({
        orders,
        firstVisible: firstDoc,
        lastVisible: lastDoc,
        hasMore: hasMore
      });
    }, (error) => {
      console.error('Error in onSnapshot:', error);
      // Error handling for admin orders subscription
      callback({
        orders: [],
        firstVisible: null,
        lastVisible: null,
        hasMore: false
      });
    });
  }
};

// Initialize marketer statistics without strict validation
export const initializeMarketerStats = async (uid) => {
  try {
    // Initializing/verifying statistics for marketer ID
    
    const statsRef = doc(db, 'marketerStats', uid);
    const statsDoc = await getDoc(statsRef);

    const defaultStats = {
      total_sales_amount: 0,
      total_confirmed_orders: 0,
      total_commission: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      marketerId: uid
    };

    if (!statsDoc.exists()) {
      await setDoc(statsRef, defaultStats);
      // Created new statistics document
    }

    return { success: true };
  } catch (error) {
    // Error handling for initializing marketer statistics
    return { success: false, error: error.message };
  }
};

// Add new function to fix existing data
export const fixMarketerData = async () => {
  try {
    // Fix marketerStats documents
    const statsQuery = query(collection(db, 'marketerStats'));
    const statsSnapshot = await getDocs(statsQuery);
    
    for (const doc of statsSnapshot.docs) {
      const data = doc.data();
      if (data.marketerUid && data.marketerUid !== doc.id) {
        // Create new document with correct ID
        await setDoc(doc(db, 'marketerStats', data.marketerUid), {
          ...data,
          updatedAt: serverTimestamp()
        });
        // Delete old document
        await deleteDoc(doc.ref);
        // Fixed marketerStats document
      }
    }

    // Fix marketerProducts documents
    const productsQuery = query(collection(db, 'marketerProducts'));
    const productsSnapshot = await getDocs(productsQuery);
    
    for (const doc of productsSnapshot.docs) {
      const data = doc.data();
      if (data.marketerUid) {
        const newDocId = `${data.marketerUid}_${Date.now()}`;
        // Create new document with correct ID pattern
        await setDoc(doc(db, 'marketerProducts', newDocId), {
          ...data,
          updatedAt: serverTimestamp()
        });
        // Delete old document
        await deleteDoc(doc.ref);
        // Fixed marketerProducts document
      }
    }

    return { success: true };
  } catch (error) {
    // Error handling for fixing marketer data
    return { success: false, error: error.message };
  }
};

function calculateOrderTotal(items) {
  return items.reduce((total, item) => {
    return total + (item.finalPrice * item.quantity);
  }, 0);
}

function calculateCommission(items) {
  return items.reduce((total, item) => {
    if (!item.marketerId) return total;
    const commission = (item.basePrice - item.promotionalPrice) * item.quantity;
    return total + commission;
  }, 0);
}

async function getNextOrderNumber(collectionName) {
  const counterRef = doc(db, 'counters', collectionName);
  try {
    let nextOrderNumber = 0;
    await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      nextOrderNumber = (counterDoc.exists() ? counterDoc.data().count : 0) + 1;
      transaction.set(counterRef, { count: nextOrderNumber }, { merge: true });
    });
    return nextOrderNumber;
  } catch (error) {
    // Error handling for getting next order number
    throw error;
  }
}

export const addMarketerProduct = async (productData) => {
  try {
    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('User not authenticated');
    }

    // Create document with marketerUid as the document ID
    const productDoc = doc(db, 'marketerProducts', `${currentUserId}_${Date.now()}`);
    
    const enrichedProductData = {
      ...productData,
      marketerId: currentUserId,
      marketerUid: currentUserId,
      createdAt: serverTimestamp()
    };

    await setDoc(productDoc, enrichedProductData);
    return { success: true, id: productDoc.id };
  } catch (error) {
    console.error('Error adding marketer product:', error);
    return { success: false, error: error.message };
  }
};

// Add new function for real-time marketer products subscription
export const subscribeToMarketerProducts = (marketerId, onUpdate, onError) => {
  try {
    const q = query(
      collection(db, 'marketerProducts'),
      where('marketerId', '==', marketerId)
    );

    // Return unsubscribe function
    return onSnapshot(q, 
      (snapshot) => {
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        onUpdate(products);
      },
      (error) => {
        console.error('Error subscribing to marketer products:', error);
        onError(error);
      }
    );
  } catch (error) {
    console.error('Error setting up marketer products subscription:', error);
    onError(error);
    return () => {}; // Return empty cleanup function
  }
};

// Contact Messages Services
export const addContactMessage = async (messageData) => {
  try {
    const docRef = await addDoc(collection(db, 'contactMessages'), {
      ...messageData,
      createdAt: serverTimestamp(),
      isRead: false
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding contact message:', error);
    return { success: false, error: error.message };
  }
};

export const getContactMessages = async () => {
  try {
    const messagesQuery = query(
      collection(db, 'contactMessages'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(messagesQuery);
    
    const messages = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    }));
    
    return { success: true, messages };
  } catch (error) {
    console.error('Error getting contact messages:', error);
    return { success: false, error: error.message };
  }
};

export const markMessageAsRead = async (messageId, isRead = true) => {
  try {
    const messageRef = doc(db, 'contactMessages', messageId);
    await updateDoc(messageRef, {
      isRead: isRead
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating message read status:', error);
    return { success: false, error: error.message };
  }
};

export const deleteContactMessage = async (messageId) => {
  try {
    await deleteDoc(doc(db, 'contactMessages', messageId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting contact message:', error);
    return { success: false, error: error.message };
  }
};

export const signInWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await auth.signInWithEmailAndPassword(email, password);
    return userCredential.user;
  } catch (error) {
    console.error('Authentication error:', error.message);
    throw error;
  }
};

export const createUserWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await auth.createUserWithEmailAndPassword(email, password);
    return userCredential.user;
  } catch (error) {
    console.error('User creation error:', error.message);
    throw error;
  }
};

export const signOut = async () => {
  try {
    await auth.signOut();
  } catch (error) {
    console.error('Sign out error:', error.message);
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    await auth.sendPasswordResetEmail(email);
  } catch (error) {
    console.error('Password reset error:', error.message);
    throw error;
  }
};

export const updateUserProfile = async (user, profile) => {
  try {
    await user.updateProfile(profile);
  } catch (error) {
    console.error('Profile update error:', error.message);
    throw error;
  }
};